import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTonConnectUI } from '@tonconnect/ui-react';
import { toNano } from '@ton/core';
import { CONTRACT_ADDRESS, IS_CONTRACT_CONFIGURED } from '../ton/config';
import { buildGiftTicketsBody } from '../ton/messages';
import { sanitizeReferrerKey } from '../ton/referral';
import { resolveOutgoingReferrerKey } from '../ton/notify';
import { walletErrorTranslationKey } from '../ton/walletError';
import { gaMoneyEvent } from '../analytics';

interface GiftTicketsState {
  pending: boolean;
  error: string | null;
  lastTxAt: number | null;
}

/**
 * Sends a GiftTickets transaction: the attached GRAM buys tickets for the
 * wallet bound to the recipient's numeric invite key, while commissions (and
 * the gifter's own registration, if new) follow the gifter's referral chain.
 * The gifter's incoming referrer key is attached as `refKey`, so a brand-new
 * gifter gets first-touch attribution exactly like a normal buy.
 */
export function useGiftTickets() {
  const { t } = useTranslation();
  const [tonConnectUI] = useTonConnectUI();
  const [state, setState] = useState<GiftTicketsState>({
    pending: false,
    error: null,
    lastTxAt: null,
  });

  const giftTickets = useCallback(
    async (recipientKey: number, amountTon: number) => {
      if (!IS_CONTRACT_CONFIGURED) {
        setState((s) => ({ ...s, error: t('lottery.notConfigured') }));
        return;
      }

      setState({ pending: true, error: null, lastTxAt: null });
      try {
        // The gifter's own referrer (same resolution as a buy): the key the
        // app was opened with, the one the bot recorded for this Telegram
        // user, or the one recorded for the connected wallet's address.
        // Only honoured on the gifter's very first transaction.
        const refKey = sanitizeReferrerKey(
          await resolveOutgoingReferrerKey(tonConnectUI.account?.address ?? null),
        );
        const body = buildGiftTicketsBody(recipientKey, refKey);
        await tonConnectUI.sendTransaction({
          validUntil: Math.floor(Date.now() / 1000) + 300,
          messages: [
            {
              address: CONTRACT_ADDRESS,
              amount: toNano(amountTon.toString()).toString(),
              payload: body.toBoc().toString('base64'),
            },
          ],
        });
        gaMoneyEvent('gift_tickets', 'GRAM', amountTon);
        setState({ pending: false, error: null, lastTxAt: Date.now() });
      } catch (e) {
        setState({
          pending: false,
          error: t(walletErrorTranslationKey(e)),
          lastTxAt: null,
        });
      }
    },
    [t, tonConnectUI],
  );

  return { ...state, giftTickets };
}
